import type { Hold } from '../../types/climb';
import type { BoardHoldRole } from '../../types/device';
import { HOLD_ROLE_LIMITS, ROLE_COLORS, ROLE_CYCLE, ROLE_ICONS } from './board-grid/roles';

/**
 * The role a circle drawn on a climb photo stands for. The same ones a board
 * problem has — a wall is a wall whether its holds are on a grid or bolted
 * wherever — so the names, the legend order, the colours and the icons are
 * shared with `board-grid/roles` rather than declared twice.
 *
 * The one thing that differs is where the role is kept: a board hold carries a
 * `role`, a photo circle carries the **colour** it is drawn in (below).
 */
export type PhotoHoldRole = BoardHoldRole;

export const PHOTO_HOLD_ROLE_CYCLE = ROLE_CYCLE;
export const PHOTO_HOLD_ROLE_ICONS = ROLE_ICONS;
/** The role's colour as hex, for a badge or a chip — the circle itself is drawn
 * in the CSS keyword below, which is where its meaning is stored. */
export const PHOTO_HOLD_ROLE_SWATCHES = ROLE_COLORS;
export const PHOTO_HOLD_ROLE_LIMITS = HOLD_ROLE_LIMITS;

/**
 * What each role is stored as in `Hold.color`, and drawn as.
 *
 * These are CSS colour keywords rather than the board legend's hex palette on
 * purpose: `Hold.color` is not just a paint value, it is how the rest of the app
 * reads a circle's meaning — `holdsForMode` shows starts and tops by testing for
 * `'green'`/`'red'`, and the route-setting board crops a card to the holds whose
 * colour is `'green'`. Storing hex here would draw the same picture and quietly
 * break both. The roles added since extend that vocabulary with keywords of
 * their own, which every renderer already passes straight to
 * `stroke`/`strokeStyle`.
 */
export const PHOTO_HOLD_ROLE_COLORS: Record<PhotoHoldRole, string> = {
  start: 'green',
  hand: 'blue',
  foot: 'orange',
  no_match: 'yellow',
  finish: 'red',
  // The hue this palette had left, and the keyword nearest the pink the board
  // lights a match in (ROLE_COLORS). White is what a matched hold wore here long
  // ago and cannot have again: a white ring disappears against a pale wall, and
  // its icon disappears against the panel it is listed on. Purple was not free
  // either — it is the right hand on a forced circuit — and magenta, fuchsia and
  // cyan are what a board flashes the hold a run is heading for.
  match: 'deeppink',
};

/**
 * What a circle reads as when its colour means nothing to us — a plain hold,
 * which is the least it can be. Not what the editor arms first: see
 * INITIAL_PHOTO_HOLD_ROLE, which is a different question with a different
 * answer.
 */
export const FALLBACK_PHOTO_HOLD_ROLE: PhotoHoldRole = 'hand';

/**
 * What the editor arms first. A climb is drawn in the order it is climbed, so
 * the first circle placed is nearly always the start — and the start is the one
 * role that must be got right, since a problem without one cannot be saved.
 */
export const INITIAL_PHOTO_HOLD_ROLE: PhotoHoldRole = 'start';

/**
 * The roles a climb on a photo cannot do without: you pull on off a start, you
 * move on something, and you top out. The middle one is satisfied by any hold
 * that is climbed on — a hand, a foot chip or a no-match — so a problem made of
 * feet and a top still counts as having holds between its ends.
 *
 * The same rule as a board problem's REQUIRED_HOLD_ROLES, loosened where a
 * photo needs it: on a board every hold is a hand unless said otherwise, while
 * on a wall a route setter marks feet as readily as hands.
 */
export const REQUIRED_PHOTO_HOLD_ROLES: PhotoHoldRole[] = ['start', 'hand', 'finish'];

/** Which of a role's stand-ins count as that role being present. */
const ROLE_STAND_INS: Record<string, PhotoHoldRole[]> = {
  start: ['start'],
  hand: ['hand', 'foot', 'no_match', 'match'],
  finish: ['finish'],
};

/**
 * Which of the required roles this set of circles is still missing, in legend
 * order — what stands between a drawing and a climb that can be saved.
 */
export function missingPhotoHoldRoles(holds: Hold[]): PhotoHoldRole[] {
  const present = new Set(holds.map(photoHoldRole));

  return REQUIRED_PHOTO_HOLD_ROLES.filter(
    (role) => !ROLE_STAND_INS[role].some((standIn) => present.has(standIn))
  );
}

/**
 * Whether a climb already has every circle of this role that it can use — four
 * starts (two hands and the feet of a sit start) or two tops.
 *
 * The same numbers the board is capped by (`HOLD_ROLE_LIMITS`), read off the
 * colours instead of a role field. A photo has no server-side cap the way a
 * board problem does, so this is the whole of the rule here: it greys the
 * colour out on the panel, and a circle drawn while it is full lands as a plain
 * hold instead — see `usePhotoHolds`.
 */
export function photoRoleAtLimit(holds: Pick<Hold, 'color'>[], role: PhotoHoldRole): boolean {
  const limit = PHOTO_HOLD_ROLE_LIMITS[role];
  if (limit === undefined) return false;

  return holds.filter((hold) => photoHoldRole(hold) === role).length >= limit;
}

const ROLE_BY_COLOR = new Map<string, PhotoHoldRole>(
  (Object.entries(PHOTO_HOLD_ROLE_COLORS) as [PhotoHoldRole, string][]).map(([role, color]) => [color, role])
);

/**
 * The role a stored circle stands for. Colours this vocabulary does not know — a
 * climb drawn before the extra roles existed, one drawn in the white a matched
 * hold used to wear, or a renderer that wrote hex — read as plain holds rather
 * than being dropped: the circle is on the wall either way, and only its meaning
 * is unknown.
 *
 * One colour is read but no longer offered: `yellow`, the no-match the board
 * still has. A circle already painted as one goes on meaning it — see
 * PHOTO_HOLD_ROLE_CYCLE.
 */
export function photoHoldRole(hold: Pick<Hold, 'color'>): PhotoHoldRole {
  return ROLE_BY_COLOR.get((hold.color ?? '').toLowerCase()) ?? FALLBACK_PHOTO_HOLD_ROLE;
}

/**
 * A round circle centred on (x, y), both in fractions of the image's natural
 * size, with `radius` given as a fraction of its **height**.
 *
 * Width and height are stored against different edges, so a circle is only
 * round when `width * naturalWidth === height * naturalHeight`. Every caller
 * that draws one goes through here rather than working that out again.
 */
export function circularHold(
  x: number,
  y: number,
  radius: number,
  color: string,
  naturalWidth: number,
  naturalHeight: number
): Hold {
  const diameterPx = 2 * radius * naturalHeight;

  return {
    x: clampFraction(x),
    y: clampFraction(y),
    width: diameterPx / naturalWidth,
    height: diameterPx / naturalHeight,
    color,
  };
}

/** A stored circle's radius, back in fractions of the image's height. */
export function holdRadius(hold: Hold): number {
  return hold.height / 2;
}

export function clampFraction(value: number): number {
  return Math.min(1, Math.max(0, value));
}
